import { useState } from "react"
import { errorToast } from "@/v2-ui/toast"
import Selector from "@/v2-backoffice-client/components/selector"
import { usePinnedResults } from "@/v2-backoffice-client/components/selector/selector.hooks"
import { useTenantQuery } from "@/v2-backoffice-client/tenant/tenant.query"
import CustomerTenantAddSelectorItem from "@/v2-backoffice-client/customer/tenant/add/CustomerTenantAddSelectorItem"

export type CustomerTenantAddSelectorDialogProps = {
  open: boolean
  onOpenChange(open: boolean): void
  externalCustomerId: string
  customerName?: string
  existingTenantIds?: number[]
}

function CustomerTenantAddSelectorDialog(
  props: CustomerTenantAddSelectorDialogProps,
) {
  const {
    open,
    onOpenChange,
    externalCustomerId,
    customerName,
    existingTenantIds = [],
  } = props

  const [search, setSearch] = useState("")
  const [addedIds, setAddedIds] = useState<Set<number>>(new Set())

  const { data, loading, error } = useTenantQuery({
    skip: !open || search.length < 2,
    variables: {
      input: {
        limit: 10,
        filters:
          search.length >= 2
            ? [{ column: "Name", contains: search }]
            : undefined,
      },
    },
  })

  errorToast(error, {
    title: "Failed to query for teams",
  })

  const tenants = data?.getTenants.items ?? []
  const pinnedTenants = usePinnedResults({
    results: tenants,
    isPinned: (t) => existingTenantIds.includes(t.Id) || addedIds.has(t.Id),
  })

  function handleOpenChange(nextOpen: boolean) {
    onOpenChange(nextOpen)
    if (!nextOpen) {
      setAddedIds(new Set())
      setSearch("")
    }
  }

  return (
    <Selector.Dialog
      open={open}
      onOpenChange={handleOpenChange}
      title={customerName ? `Add team to ${customerName}` : "Add team"}
      description="Search for the team you want to add, then click on it to add it to the customer."
    >
      <Selector.Search
        placeholder="Search by team name..."
        onSearch={setSearch}
      />

      <Selector.List
        emptyLabel="No teams found"
        isLoading={loading}
        error={error}
        noResults={tenants.length === 0}
        pinned={pinnedTenants.map((tenant) => (
          <CustomerTenantAddSelectorItem
            key={tenant.Id}
            added
            tenant={tenant}
            externalCustomerId={externalCustomerId}
            onSelect={() => {
              setAddedIds((prev) => new Set(prev).add(tenant.Id))
            }}
          />
        ))}
      >
        {tenants.map((tenant) => {
          const isAdded =
            existingTenantIds.includes(tenant.Id) || addedIds.has(tenant.Id)
          return (
            <CustomerTenantAddSelectorItem
              key={tenant.Id}
              added={isAdded}
              tenant={tenant}
              externalCustomerId={externalCustomerId}
              onSelect={() => {
                setAddedIds((prev) => new Set(prev).add(tenant.Id))
              }}
            />
          )
        })}
      </Selector.List>
    </Selector.Dialog>
  )
}

export default CustomerTenantAddSelectorDialog
